import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { LOGO } from "@/data/exercises";
import { POSTER } from "@/data/gardiens";
import { GardienBadge } from "@/components/GardienBadge";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const back = new URLSearchParams(window.location.search).get("retour") || "";
      navigate(back.startsWith("/ecouter/") ? back : u.space === "residence" ? "/nouvelles" : u.role === "family" ? "/famille" : "/espaces");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row font-care">
      <div className="hidden md:flex md:w-1/2 bg-background flex-col border-r border-border/60">
        <div className="grain overflow-hidden">
          <img src={POSTER} alt="Les Gardiens des Souvenirs et des forces intérieures" className="w-full h-auto block" data-testid="login-poster" />
        </div>
        <div className="px-12 lg:px-14 py-10 flex-1 flex flex-col">
          <h1 className="font-serif-display italic font-light text-4xl lg:text-5xl leading-[1.08] tracking-tight text-foreground/90 mb-5">
            Stimulation et accompagnement pour nos proches / résidents.
          </h1>
          <p className="text-lg text-foreground/70 max-w-md leading-relaxed">
            Un espace pensé pour les proches / soignants de personnes atteintes d'Alzheimer,
            d'autisme, de maladies dégénératives ou apparentées.
          </p>
          <GardienBadge id="leontine" eyebrow="Gardienne de l'accueil" className="mt-8 max-w-xl" testId="login-gardien-leontine" />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-accent/30">
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-card rounded-3xl border border-border/60 shadow-[0_20px_40px_rgb(0,0,0,0.05)] p-8 sm:p-10"
          data-testid="login-form"
        >
          <img src={LOGO} alt="Passerelle" className="h-20 w-auto mb-6 mx-auto" data-testid="login-logo" />
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground text-center">Espace sécurisé</p>
          <h2 className="font-serif-display text-3xl text-center mt-1 mb-6">Connexion</h2>

          {error && (
            <div
              className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="soignant@demo.fr"
                className="mt-1 h-12"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 h-12"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 mt-6 text-base text-white rounded-full transition-transform hover:scale-[1.02] border-0"
            style={{ background: "#2E8EC2" }}
            data-testid="login-submit-button"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Se connecter"}
          </Button>

          <p className="text-center text-sm mt-4">
            <Link to="/mot-de-passe-oublie" className="text-emerald-700 font-semibold hover:underline" data-testid="forgot-password-link">
              Mot de passe oublié ?
            </Link>
          </p>

          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="login-trial-block">
            <p className="text-center text-sm font-semibold text-emerald-800 mb-3">Essai gratuit 7 jours, sans carte bancaire</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Link to="/inscription?espace=famille" data-testid="trial-start-famille"
                className="text-center rounded-full bg-white border-2 border-emerald-200 text-emerald-800 font-semibold text-sm px-3 py-2.5 hover:bg-emerald-50 transition-colors">
                Aidant & Famille
              </Link>
              <Link to="/inscription?espace=soignant" data-testid="trial-start-soignant"
                className="text-center rounded-full bg-white border-2 border-emerald-200 text-emerald-800 font-semibold text-sm px-3 py-2.5 hover:bg-emerald-50 transition-colors">
                Soignant & Patient
              </Link>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-5">
            Pas encore de compte ?{" "}
            <Link to="/inscription" className="text-emerald-700 font-semibold" data-testid="go-register-link">
              Créer un compte
            </Link>
          </p>

          <Link to="/rejoindre" data-testid="go-join-family-link"
            className="mt-5 block rounded-2xl border border-sky-200 bg-sky-50/60 p-4 text-left hover:bg-sky-50 transition-colors">
            <span className="block text-xs uppercase tracking-[0.2em] text-sky-700 mb-1">Famille d'un résident</span>
            <span className="block font-semibold text-gray-800">Votre proche vit en établissement ?</span>
            <span className="block text-sm text-gray-600">J'ai un code de l'équipe soignante : suivre ses nouvelles gratuitement.</span>
          </Link>

          <p className="text-center text-sm text-gray-500 mt-3">
            Besoin d'aide ?{" "}
            <Link to="/contact" className="text-emerald-700 font-semibold hover:underline" data-testid="login-contact-link">
              Nous contacter
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
